
import { useState, useMemo } from 'react';
import InteractiveChart from './InteractiveChart';
import Card from '../base/Card';
import { useIngresosOptimistic } from '../../modules/ingresos/hooks/useIngresosOptimistic';
import { useEgresosOptimistic } from '../../modules/egresos/hooks/useEgresosOptimistic';

interface ChartData {
  label: string;
  value: number;
  color: string;
  details?: { [key: string]: number };
}

export default function DashboardSection() {
  const [selectedPeriod, setSelectedPeriod] = useState('mensual');
  
  // Obtener datos reales de Firebase
  const { ingresos, isLoading: ingresosLoading, error: ingresosError } = useIngresosOptimistic();
  const { egresos, isLoading: egresosLoading, error: egresosError } = useEgresosOptimistic();

  // Procesar datos reales de ingresos agrupados por categoría
  const incomeData: ChartData[] = useMemo(() => {
    if (!ingresos || ingresos.length === 0) {
      return []; // Retornar vacío si no hay datos
    }

    // Agrupar ingresos por categoría
    const groupedIngresos = ingresos.reduce((acc, ingreso) => {
      const categoria = ingreso.categoria || 'Sin categoría';
      if (!acc[categoria]) {
        acc[categoria] = {
          total: 0,
          items: []
        };
      }
      acc[categoria].total += ingreso.monto;
      acc[categoria].items.push(ingreso);
      return acc;
    }, {} as Record<string, { total: number; items: typeof ingresos }>);

    // Convertir a formato ChartData con colores
    const colors = ['#10b981', '#059669', '#047857', '#065f46', '#064e3b'];
    return Object.entries(groupedIngresos).map(([categoria, data], index) => ({
      label: categoria,
      value: data.total,
      color: colors[index % colors.length],
      details: data.items.reduce((details, item) => {
        const key = item.descripcion || item.concepto || `Item ${item.id?.substring(0, 8)}`;
        details[key] = item.monto;
        return details;
      }, {} as { [key: string]: number })
    }));
  }, [ingresos]);
  // Procesar datos reales de egresos agrupados por categoría
  const expenseData: ChartData[] = useMemo(() => {
    if (!egresos || egresos.length === 0) {
      return []; // Retornar vacío si no hay datos
    }

    // Agrupar egresos por categoría
    const groupedEgresos = egresos.reduce((acc, egreso) => {
      const categoria = egreso.categoria || 'Sin categoría';
      if (!acc[categoria]) {
        acc[categoria] = {
          total: 0,
          items: []
        };
      }
      acc[categoria].total += egreso.monto;
      acc[categoria].items.push(egreso);
      return acc;
    }, {} as Record<string, { total: number; items: typeof egresos }>);

    // Convertir a formato ChartData con colores
    const colors = ['#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d'];
    return Object.entries(groupedEgresos).map(([categoria, data], index) => ({
      label: categoria,
      value: data.total,
      color: colors[index % colors.length],
      details: data.items.reduce((details, item) => {
        const key = item.descripcion || item.concepto || `Item ${item.id?.substring(0, 8)}`;
        details[key] = item.monto;
        return details;
      }, {} as { [key: string]: number })
    }));
  }, [egresos]);
  
  // Calcular totales solo con datos reales
  const totalIncome = incomeData.reduce((sum, item) => sum + item.value, 0);
  const totalExpense = expenseData.reduce((sum, item) => sum + item.value, 0);
  const balance = totalIncome - totalExpense;
  
  // Loading and error states
  const isLoading = ingresosLoading || egresosLoading;
  const hasError = ingresosError || egresosError;
  
  // const balanceData = [
  //   { label: 'Ingresos Totales', value: totalIncome, color: '#10b981' },
  //   { label: 'Egresos Totales', value: totalExpense, color: '#ef4444' }
  // ];
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount);
  };
  
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Dashboard Financiero Interactivo
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Visualiza y analiza tus finanzas con gráficos dinámicos. Haz clic en cualquier categoría 
            para ver el desglose detallado de tus ingresos y gastos.
          </p>
          
          {/* Estado de datos */}
          <div className="mt-6 flex justify-center">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm">
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-600">Cargando datos...</span>
                </>
              ) : hasError ? (
                <>
                  <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                  <span className="text-sm text-red-600">Error al cargar datos</span>
                </>
              ) : incomeData.length === 0 && expenseData.length === 0 ? (
                <>
                  <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-yellow-700">No hay datos financieros</span>
                </>
              ) : (
                <>
                  <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-700">
                    Datos reales: {incomeData.length} ingresos, {expenseData.length} egresos
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mensaje de datos vacíos */}
        {!isLoading && !hasError && incomeData.length === 0 && expenseData.length === 0 && (
          <div className="mb-12">
            <Card className="text-center py-12">
              <div className="text-6xl mb-4">📊</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                No hay datos financieros
              </h3>
              <p className="text-gray-600 mb-6">
                Comienza agregando tus primeros ingresos y gastos para ver tu dashboard financiero.
              </p>
              <div className="flex justify-center gap-4">
                <button className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                  Agregar Ingreso
                </button>
                <button className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                  Agregar Gasto
                </button>
              </div>
            </Card>
          </div>
        )}
        
        {/* Filtros de período - Solo mostrar si hay datos */}
        {(incomeData.length > 0 || expenseData.length > 0) && (
        <div className="flex justify-center mb-8">
          <div className="flex bg-white rounded-lg p-1 shadow-sm">
            {['mensual', 'trimestral', 'anual'].map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap capitalize ${
                  selectedPeriod === period
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-emerald-600'
                }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>
        )}
        
        {/* Resumen de balance - Solo mostrar si hay datos */}
        {(incomeData.length > 0 || expenseData.length > 0) && (
        <div className="mb-12">
          <Card className="text-center">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Balance {selectedPeriod}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4">
                <div className="text-3xl font-bold text-emerald-600 mb-2">
                  {formatCurrency(totalIncome)}
                </div>
                <div className="text-gray-600">Ingresos Totales</div>
              </div>
              <div className="p-4">
                <div className="text-3xl font-bold text-red-600 mb-2">
                  {formatCurrency(totalExpense)}
                </div>
                <div className="text-gray-600">Egresos Totales</div>
              </div>
              <div className="p-4">
                <div className={`text-3xl font-bold mb-2 ${balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(balance)}
                </div>
                <div className="text-gray-600">Balance Final</div>
                <div className={`text-sm mt-1 ${balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {balance >= 0 ? '✓ Superávit' : '⚠ Déficit'}
                </div>
              </div>
            </div>
          </Card>
        </div>
        )}
        
        {/* Gráficos interactivos - Solo mostrar si hay datos */}
        {(incomeData.length > 0 || expenseData.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <InteractiveChart
            title="Análisis de Ingresos"
            data={incomeData}
            type="income"
          />
          
          <InteractiveChart
            title="Análisis de Egresos"
            data={expenseData}
            type="expense"
          />
        </div>
        )}
        
        {/* Gráfico comparativo - Solo mostrar si hay datos */}
        {(incomeData.length > 0 || expenseData.length > 0) && (
        <div className="mb-8">
          <Card>
            <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">
              Comparativo Ingresos vs Egresos
            </h3>
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-emerald-700">Ingresos</span>
                  <span className="font-bold text-emerald-600">{formatCurrency(totalIncome)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="h-4 rounded-full bg-emerald-500 transition-all duration-1000"
                    style={{ width: '100%' }}
                  ></div>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-red-700">Egresos</span>
                  <span className="font-bold text-red-600">{formatCurrency(totalExpense)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="h-4 rounded-full bg-red-500 transition-all duration-1000"
                    style={{ width: `${(totalExpense / totalIncome) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-700">Balance:</span>
                  <span className={`text-2xl font-bold ${balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(balance)}
                  </span>
                </div>
                <div className="text-center mt-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    balance >= 0 
                      ? 'bg-emerald-100 text-emerald-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {balance >= 0 ? (
                      <>
                        <i className="ri-arrow-up-line mr-1"></i>
                        Situación Positiva
                      </>
                    ) : (
                      <>
                        <i className="ri-arrow-down-line mr-1"></i>
                        Revisar Gastos
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>
        )}
      </div>
    </section>
  );
}
